<template>
  <v-dialog :model-value="modelValue" max-width="480px" persistent @update:model-value="emitClose">
    <v-card class="user-profile-dialog">
      <v-card-title class="dialog-header">
        <span class="dialog-header-title">
          <v-icon class="mr-2">mdi-account-cog-outline</v-icon>
          내 정보
        </span>
        <button
          type="button"
          class="dialog-close"
          aria-label="닫기"
          :disabled="savingProfile || savingPassword"
          @click="emitClose(false)"
        >
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </v-card-title>

      <v-card-text class="profile-body">
        <LoadingOverlay
          :show="savingProfile || savingPassword || loading"
          :text="savingPassword ? '비밀번호 변경 중...' : savingProfile ? '저장 중...' : '불러오는 중...'"
        />

        <section class="profile-section">
          <h4 class="section-title">기본 정보</h4>
          <div class="field-row">
            <label class="field-label">아이디</label>
            <input class="native-input disabled" type="text" :value="form.u_id" disabled />
          </div>
          <div class="field-row">
            <label class="field-label">이름 *</label>
            <input v-model="form.u_name" class="native-input" type="text" placeholder="이름" />
          </div>
          <div class="field-row">
            <label class="field-label">이메일</label>
            <input v-model="form.u_email" class="native-input" type="email" placeholder="email@example.com" />
          </div>
          <div class="field-row">
            <label class="field-label">연락처</label>
            <input v-model="form.u_phone" class="native-input" type="tel" placeholder="010-0000-0000" />
          </div>
          <div class="field-actions">
            <v-btn
              variant="flat"
              class="btn-save"
              :loading="savingProfile"
              :disabled="loading || savingPassword"
              @click="saveProfile"
            >
              정보 저장
            </v-btn>
          </div>
        </section>

        <section class="profile-section">
          <h4 class="section-title">비밀번호 변경</h4>
          <div class="field-row">
            <label class="field-label">현재 비밀번호 *</label>
            <input
              v-model="pwForm.oldPw"
              class="native-input"
              type="password"
              autocomplete="current-password"
              placeholder="현재 비밀번호"
            />
          </div>
          <div class="field-row">
            <label class="field-label">새 비밀번호 *</label>
            <input
              v-model="pwForm.newPw"
              class="native-input"
              type="password"
              autocomplete="new-password"
              placeholder="새 비밀번호 (4자 이상)"
            />
          </div>
          <div class="field-row">
            <label class="field-label">새 비밀번호 확인 *</label>
            <input
              v-model="pwForm.newPwConfirm"
              class="native-input"
              type="password"
              autocomplete="new-password"
              placeholder="새 비밀번호 다시 입력"
            />
          </div>
          <div class="field-actions">
            <v-btn
              variant="flat"
              class="btn-edit"
              :loading="savingPassword"
              :disabled="loading || savingProfile"
              @click="savePassword"
            >
              비밀번호 변경
            </v-btn>
          </div>
        </section>
      </v-card-text>

      <v-card-actions class="dialog-actions">
        <v-spacer />
        <v-btn variant="text" class="btn-close" :disabled="savingProfile || savingPassword" @click="emitClose(false)">
          닫기
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { reactive, ref, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { userApi } from '@/services/api';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const store = useStore();
const currentUser = computed(() => store.getters.currentUser);
const userIdx = computed(() => Number(currentUser.value?.idx || 0));

const loading = ref(false);
const savingProfile = ref(false);
const savingPassword = ref(false);

const form = reactive({
  u_id: '',
  u_name: '',
  u_email: '',
  u_phone: '',
});

const pwForm = reactive({
  oldPw: '',
  newPw: '',
  newPwConfirm: '',
});

const emitClose = (val: boolean) => {
  if (savingProfile.value || savingPassword.value) return;
  emit('update:modelValue', val);
};

const resetPasswordForm = () => {
  pwForm.oldPw = '';
  pwForm.newPw = '';
  pwForm.newPwConfirm = '';
};

const loadProfile = async () => {
  if (!userIdx.value) return;
  loading.value = true;
  try {
    const res = await userApi.getById(userIdx.value);
    if (res.data?.success && res.data.data) {
      const u = res.data.data;
      form.u_id = u.u_id || currentUser.value?.id || '';
      form.u_name = u.u_name || '';
      form.u_email = u.u_email || '';
      form.u_phone = u.u_phone || '';
    } else {
      form.u_id = currentUser.value?.id || '';
      form.u_name = currentUser.value?.name || '';
      form.u_email = currentUser.value?.email || '';
      form.u_phone = currentUser.value?.phone || '';
    }
  } catch (error: any) {
    if (error?.response?.status === 401) return;
    form.u_id = currentUser.value?.id || '';
    form.u_name = currentUser.value?.name || '';
    form.u_email = currentUser.value?.email || '';
    form.u_phone = currentUser.value?.phone || '';
    const message = error?.response?.data?.message || error?.message || '불러오기 실패';
    store.dispatch('showError', `내 정보 조회 실패: ${message}`);
  } finally {
    loading.value = false;
  }
};

const saveProfile = async () => {
  if (!userIdx.value) return;
  if (!form.u_name.trim()) {
    store.dispatch('showError', '이름을 입력해주세요.');
    return;
  }
  savingProfile.value = true;
  try {
    await userApi.update(userIdx.value, {
      u_name: form.u_name.trim(),
      u_email: form.u_email.trim(),
      u_phone: form.u_phone.trim(),
    });
    store.dispatch('updateCurrentUserProfile', {
      name: form.u_name.trim(),
      email: form.u_email.trim(),
      phone: form.u_phone.trim(),
    });
    store.dispatch('showSuccess', '정보가 저장되었습니다.');
  } catch (error: any) {
    if (error?.response?.status === 401) return;
    const message = error?.response?.data?.message || error?.message || '저장 실패';
    store.dispatch('showError', `정보 저장 실패: ${message}`);
  } finally {
    savingProfile.value = false;
  }
};

const savePassword = async () => {
  if (!userIdx.value) return;
  if (!pwForm.oldPw || !pwForm.newPw) {
    store.dispatch('showError', '현재 비밀번호와 새 비밀번호를 입력해주세요.');
    return;
  }
  if (pwForm.newPw.length < 4) {
    store.dispatch('showError', '새 비밀번호는 4자 이상이어야 합니다.');
    return;
  }
  if (pwForm.newPw !== pwForm.newPwConfirm) {
    store.dispatch('showError', '새 비밀번호 확인이 일치하지 않습니다.');
    return;
  }
  if (pwForm.oldPw === pwForm.newPw) {
    store.dispatch('showError', '새 비밀번호는 현재 비밀번호와 달라야 합니다.');
    return;
  }

  savingPassword.value = true;
  try {
    const res = await userApi.changePassword(userIdx.value, pwForm.oldPw, pwForm.newPw);
    if (res.data?.success === false) {
      store.dispatch('showError', res.data.message || '비밀번호 변경에 실패했습니다.');
      return;
    }
    store.dispatch('showSuccess', '비밀번호가 변경되었습니다.');
    resetPasswordForm();
  } catch (error: any) {
    if (error?.response?.status === 401) return;
    const message = error?.response?.data?.message || error?.message || '변경 실패';
    store.dispatch('showError', `비밀번호 변경 실패: ${message}`);
  } finally {
    savingPassword.value = false;
  }
};

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      resetPasswordForm();
      loadProfile();
    }
  }
);
</script>
